import { signal, WritableSignal, computed } from "@angular/core";
import { GridApi, GridOptions } from "ag-grid-enterprise";
import { type ISymbolView } from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { SymbolTableSubscription, TableSource } from "./symbol-table.subscription";

// Helper interface to satisfy template lookups without mutating base types
export interface SymbolCardView {
  id: string;
  code: string;
  name: string;
  description: string;
  baseSymbol: string;
  icon: string;
  rawSymbol: ISymbolView;
  balance?: number;
}

export class SymbolTable extends SymbolTableSubscription {
  gridApi: GridApi;
  options: GridOptions<TableSource>;
  selectedSymbol: WritableSignal<ISymbolView> = signal<ISymbolView>(null);
  private selectedRowIndex: number;
  private pendingSelection: ISymbolView | null = null;

  // Updated computed signal mapping with the missing balance property fully assigned
  readonly flatData = computed<SymbolCardView[]>(() => {
    const rawList = this.data() as any[];
    if (!rawList) {
      return [];
    }

    const seen = new Set<string>();
    return rawList.reduce<SymbolCardView[]>((acc, item) => {
      const sym = item.symbol as any;
      const code: string = sym?.code || "";
      if (code && seen.has(code)) {
        return acc;
      }
      if (code) {
        seen.add(code);
      }
      acc.push({
        id: code || Math.random().toString(),
        code,
        name: sym?.name || "",
        description: sym?.c_asOf?.date?.date || "",
        baseSymbol: sym?.baseSymbol || item.rate?.cross || "",
        icon: sym?.icon || "token-symbol",
        rawSymbol: item.symbol as ISymbolView,
        balance: item.balance ?? item.rate?.value ?? 0,
      });
      return acc;
    }, []);
  });

  constructor() {
    super();

    // Your exact original grid configurations left completely intact
    this.options = {
      rowSelection: "single",
      columnDefs: [],
      suppressCellFocus: true,
      suppressContextMenu: true,
      onGridReady: (params) => {
        this.gridApi = params.api;
        this.onRowClicked(this.selectedSymbol());
      },
      onRowDataUpdated: () => {
        if (this.pendingSelection) {
          this.performSelection(this.pendingSelection);
          this.pendingSelection = null;
        }
      },
    };
  }

  // Your exact original selectSymbolRow logic
  selectSymbolRow(symbol: ISymbolView) {
    if (!this.gridApi) {
      return;
    }
    this.pendingSelection = symbol;
    this.performSelection(symbol);
  }

  // Your exact original performSelection logic
  private performSelection(symbol: ISymbolView) {
    if (!this.gridApi || !symbol) {
      return;
    }
    this.selectedRowIndex = null;
    this.gridApi.forEachNode((node) => {
      if (node.data?.symbol?.code === symbol.code) {
        this.selectedSymbol.set(symbol);
        node.setSelected(true);
        this.selectedRowIndex = node.rowIndex;
      }
    });
    this.gridApi.redrawRows();
  }

  // Your exact original onRowClicked logic
  onRowClicked(symbol: ISymbolView): void {
    if (!symbol) {
      return;
    }
    this.selectedSymbol.set(symbol);
    if (this.gridApi) {
      this.gridApi.forEachNode((node) => {
        if (node.data?.symbol?.code === symbol.code) {
          this.selectedRowIndex = node.rowIndex;
        }
      });
      this.gridApi.redrawRows();
    }
  }
}
